/**
 * useImageCollection Hook
 *
 * Custom hook for Image Collection ("Bildersammlung") data fetching.
 * Uses URL-based filter state for shareable/bookmarkable filters.
 */
import { useQuery } from '@tanstack/react-query';
import { useSearchParams } from 'react-router-dom';
import { useMemo, useCallback } from 'react';

import { getImageCollection, getImageCollectionFilters } from '@/lib/api/image-collection';
import type { CollectionFilters } from '@/types/image-collection';

interface UseImageCollectionOptions {
  productId: string;
  enabled?: boolean;
}

export function useImageCollection({ productId, enabled = true }: UseImageCollectionOptions) {
  const [searchParams, setSearchParams] = useSearchParams();

  // Parse filters from URL
  const filters = useMemo<CollectionFilters>(() => {
    const tags = searchParams.get('tags');
    const source = searchParams.get('source');
    const channel = searchParams.get('channel');
    const status = searchParams.get('status');
    const page = searchParams.get('page');
    const perPage = searchParams.get('per_page');

    return {
      tags: tags ? tags.split(',') : undefined,
      source: source as CollectionFilters['source'],
      channel: channel || undefined,
      status: status as CollectionFilters['status'],
      page: page ? parseInt(page, 10) : 1,
      per_page: perPage ? parseInt(perPage, 10) : 50,
    };
  }, [searchParams]);

  // Update filters in URL
  const updateFilters = useCallback(
    (newFilters: Partial<CollectionFilters>) => {
      const params = new URLSearchParams(searchParams);

      // Update or remove each filter
      Object.entries(newFilters).forEach(([key, value]) => {
        if (value === undefined || value === null || value === '') {
          params.delete(key);
        } else if (Array.isArray(value)) {
          if (value.length === 0) {
            params.delete(key);
          } else {
            params.set(key, value.join(','));
          }
        } else {
          params.set(key, value.toString());
        }
      });

      setSearchParams(params, { replace: true });
    },
    [searchParams, setSearchParams]
  );

  // Clear all filters
  const clearFilters = useCallback(() => {
    setSearchParams({}, { replace: true });
  }, [setSearchParams]);

  // Load image collection
  const collectionQuery = useQuery({
    queryKey: ['image-collection', productId, filters],
    queryFn: () => getImageCollection(productId, filters),
    enabled: enabled && !!productId,
    staleTime: 30000, // 30 seconds
  });

  // Load filter options
  const filterOptionsQuery = useQuery({
    queryKey: ['image-collection-filters', productId],
    queryFn: () => getImageCollectionFilters(productId),
    enabled: enabled && !!productId,
    staleTime: 300000, // 5 minutes (tags/channels don't change often)
  });

  // Active filter count (for badge)
  const activeFilterCount = useMemo(() => {
    let count = 0;
    if (filters.tags && filters.tags.length > 0) count++;
    if (filters.source) count++;
    if (filters.channel) count++;
    if (filters.status) count++;
    return count;
  }, [filters]);

  return {
    // Data
    images: collectionQuery.data?.images || [],
    totalCount: collectionQuery.data?.total_count || 0,
    page: collectionQuery.data?.page || 1,
    perPage: collectionQuery.data?.per_page || 50,
    totalPages: collectionQuery.data?.total_pages || 0,
    filterOptions: filterOptionsQuery.data?.filter_options,

    // Loading states
    isLoading: collectionQuery.isLoading,
    isLoadingFilters: filterOptionsQuery.isLoading,
    isFetching: collectionQuery.isFetching,

    // Error states
    error: collectionQuery.error,
    filterOptionsError: filterOptionsQuery.error,

    // Filter state
    filters,
    activeFilterCount,

    // Filter actions
    updateFilters,
    clearFilters,

    // Refetch
    refetch: collectionQuery.refetch,
    refetchFilters: filterOptionsQuery.refetch,
  };
}
